/**
 *
 * Returns MUI sx layout styles for a form item based on alignment and layout mode.
 * Supports column, inline, and two-column grid variants (fit or stretch). When an
 * unsupported combination is provided, the function may return undefined.
 *
 * @param {object} [style] - Layout configuration.
 * @param {'left'|'center'|'right'} [style.align='left'] - Horizontal alignment of the item or grid content.
 * @param {'column'|'inline'|'2-columns-fit'|'2-columns-stretch'} [style.layout='column'] - Layout mode:
 * - column: vertical stack; uses alignSelf for left/center/right.
 * - inline: horizontal row; uses justifyContent for left/center/right.
 * - 2-columns-fit: two columns sized to content (auto auto) on sm+, single column on xs.
 * - 2-columns-stretch: two equal-width columns on sm+, single column on xs.
 * @param {boolean} [style.inline] - @deprecated Use style.layout="inline" instead.
 * @returns {SxProps<Theme>|undefined} The computed MUI sx object describing layout styles, or undefined if no mapping applies.
 */
export function getItemLayoutSx(style) {
    var _a, _b;
    const align = (_a = style === null || style === void 0 ? void 0 : style.align) !== null && _a !== void 0 ? _a : 'left';
    const layout = (_b = style === null || style === void 0 ? void 0 : style.layout) !== null && _b !== void 0 ? _b : ((style === null || style === void 0 ? void 0 : style.inline) ? 'inline' : 'column');
    switch (layout) {
        case 'column':
            switch (align) {
                case 'left':
                    return { alignSelf: 'flex-start', gap: 1 };
                case 'center':
                    return { alignSelf: 'center', gap: 1 };
                case 'right':
                    return { alignSelf: 'flex-end', gap: 1 };
            }
            break;
        case 'inline':
            switch (align) {
                case 'left':
                    return { flexDirection: 'row', gap: 1, justifyContent: 'flex-start' };
                case 'center':
                    return { flexDirection: 'row', gap: 1, justifyContent: 'center' };
                case 'right':
                    return { flexDirection: 'row', gap: 1, justifyContent: 'flex-end' };
            }
            break;
        case '2-columns-fit':
            switch (align) {
                case 'left':
                    return {
                        alignSelf: 'flex-start',
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: `100%`,
                            sm: `auto auto`,
                        },
                        gap: 1,
                        justifyItems: 'stretch',
                    };
                case 'center':
                    return {
                        alignSelf: 'center',
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: `100%`,
                            sm: `auto auto`,
                        },
                        gap: 1,
                        justifyItems: 'stretch',
                    };
                case 'right':
                    return {
                        alignSelf: 'flex-end',
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: `100%`,
                            sm: `auto auto`,
                        },
                        gap: 1,
                        justifyItems: 'stretch',
                    };
            }
            break;
        case '2-columns-stretch':
            switch (align) {
                case 'left':
                    return {
                        display: 'grid',
                        gridTemplateColumns: (theme) => ({
                            xs: `100%`,
                            sm: `calc(50% - ${theme.spacing(0.5)}) calc(50% - ${theme.spacing(0.5)})`,
                        }),
                        gap: 1,
                        justifyItems: 'flex-start',
                    };
                case 'center':
                    return {
                        display: 'grid',
                        gridTemplateColumns: (theme) => ({
                            xs: `100%`,
                            sm: `calc(50% - ${theme.spacing(0.5)}) calc(50% - ${theme.spacing(0.5)})`,
                        }),
                        gap: 1,
                        justifyItems: 'center',
                    };
                case 'right':
                    return {
                        display: 'grid',
                        gridTemplateColumns: (theme) => ({
                            xs: `100%`,
                            sm: `calc(50% - ${theme.spacing(0.5)}) calc(50% - ${theme.spacing(0.5)})`,
                        }),
                        gap: 1,
                        justifyItems: 'flex-end',
                    };
            }
    }
}
